/**
 * Maps API interview/self-schedule response to the wizard's internal payload shape.
 * Used for edit mode: fetch existing interview → populate form fields.
 */
import type { InterviewDetails } from '@/api/useInterviews';
import type { ScheduleInterviewPayload } from '../config/schedule.types';
import type { FindTimeValue } from '../calendar/selfSchedule';

/** Reverse of `scheduleTypeFor` — backend string → find-time preset. */
function findTimeValueFromScheduleType(
  scheduleType: string | undefined
): FindTimeValue {
  switch (scheduleType) {
    case 'Next weekday':
      return 'next_weekday';
    case 'Next 2 weekdays':
      return 'next_2_weekdays';
    case 'Next 3 weekdays':
      return 'next_3_weekdays';
    case 'Next 4 weekdays':
      return 'next_4_weekdays';
    case 'Next 5 weekdays':
      return 'next_5_weekdays';
    case 'Next week':
      return 'next_week';
    case 'Next 2 weeks':
      return 'next_2_weeks';
    default:
      return null;
  }
}

/**
 * Map an existing interview from the API into the wizard payload.
 * Converts API field names to form field names.
 */
export function hydrateDetailsFromApi(
  interview: InterviewDetails
): Record<string, unknown> {
  const participants = interview.participants ?? [];
  const participantIds =
    interview.participant_ids ?? participants.map((p) => p.id);

  // /ats_interviews/:id doesn't return a top-level `duration` (the
  // backend stores it per attendee). Compute from start_time/end_time
  // when the field is missing so edit-mode shows the correct value
  // instead of falling back to 30.
  let duration =
    typeof interview.duration === 'number' && interview.duration > 0
      ? interview.duration
      : 30;
  if (
    !(typeof interview.duration === 'number' && interview.duration > 0) &&
    typeof interview.start_time === 'string' &&
    typeof interview.end_time === 'string'
  ) {
    const startMs = Date.parse(interview.start_time);
    const endMs = Date.parse(interview.end_time);
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      duration = Math.round((endMs - startMs) / 60000);
    }
  }

  // HackerRank template: API may return nested under hackerrank_interview
  // or at the top level. Mirror the plan-fallback shape used in defaults.
  const hrNested = interview.hackerrank_interview as
    | { ext_interview_template_id?: string | number }
    | undefined;
  const hrTemplate =
    hrNested?.ext_interview_template_id ??
    (interview.ext_interview_template_id as string | number | undefined);
  const ext_interview_template_id =
    hrTemplate !== undefined && hrTemplate !== null ? String(hrTemplate) : null;

  return {
    interviewName: interview.name ?? '',
    interview_type: interview.kind ?? interview.interview_kind ?? 'one_on_one',
    participant_ids: participantIds.map(String),
    duration,
    location: interview.location ?? 'zoom',
    host_id: interview.host_id ? String(interview.host_id) : '',
    ats_office_id: interview.ats_office_id
      ? String(interview.ats_office_id)
      : '',
    calendar_visibility:
      interview.calendar_visibility === 'private' ||
      interview.calendar_visibility === true,
    note_taker_enabled: Boolean(interview.note_taker_enabled),
    fill_with_ai_enabled: Boolean(interview.fill_with_ai_enabled),
    // Meeting rooms
    book_meeting_room:
      Array.isArray(interview.meeting_rooms) &&
      interview.meeting_rooms.length > 0,
    meeting_room_ids: (interview.meeting_rooms ?? []).map((r) => r.email ?? ''),
    // Booked slot (for manual mode)
    ...(interview.start_time && interview.end_time
      ? {
          bookedSlot: {
            start: interview.start_time,
            end: interview.end_time,
            duration,
          },
        }
      : {}),
    // Self-schedule slots. Accept either `{start_at, end_at}` (the
    // shape our payload builder writes) or `{start_time, end_time}`
    // (legacy / plan-defaults shape) — backend responses in the wild
    // use both. Drop empty / malformed rows.
    selfScheduleSlots: Array.isArray(interview.time_slots)
      ? interview.time_slots
          .map((slot) => {
            const start = slot.start_at ?? slot.start_time ?? '';
            const end = slot.end_at ?? slot.end_time ?? '';
            if (!start || !end) return null;
            return {
              start,
              end,
              duration,
              is_custom: Boolean(slot.is_custom),
              ...(slot.override_type
                ? { override_type: slot.override_type }
                : {}),
            };
          })
          .filter(
            (
              s
            ): s is {
              start: string;
              end: string;
              duration: number;
              is_custom: boolean;
              override_type?: string;
            } => s !== null
          )
      : [],
    // Find Time preset reconstructed from legacy `schedule_type`.
    findTimeValue: findTimeValueFromScheduleType(
      typeof interview.schedule_type === 'string'
        ? interview.schedule_type
        : undefined
    ),
    // Participant default options for re-rendering selected chips
    participant_default_options: participants.map((p) => ({
      value: String(p.id),
      label: p.name ?? p.email ?? `User ${p.id}`,
      image: p.avatar_url,
    })),
    timezone: interview.timezone,
    ext_interview_template_id,
  };
}

interface BackendCommTemplate {
  id?: number;
  name?: string | null;
  subject?: string | null;
  body?: string | null;
  attachments?: unknown[];
  category?: string;
  channel?: string;
}

interface BackendEmailCommConfig {
  subject?: string | null;
  body?: string | null;
  attachments?: unknown[];
  comm_template_id?: number;
  from_user_id?: number;
  cc_user_ids?: number[];
  bcc_user_ids?: number[];
}

/**
 * Map the top-level comm shape returned by GET /ats_interviews/:id
 * (or /ats_interviews/new, which emits the same keys for plan
 * defaults) into the InterviewActivity-shape payload that
 * `hydrateCommunicationStateFromPayload` consumes. Keep the mapping
 * permissive — any missing field falls back to defaults inside the
 * hydrator.
 */
export function hydrateCommunicationFromApi(
  interview: InterviewDetails | Record<string, unknown>
): Record<string, unknown> {
  const asRecord = interview as Record<string, unknown>;
  const candidateCal = asRecord.candidate_comm_template as
    | BackendCommTemplate
    | undefined;
  const reviewerCal = asRecord.reviewer_comm_template as
    | BackendCommTemplate
    | undefined;
  const candidateEmail = asRecord.candidate_email_config as
    | BackendEmailCommConfig
    | undefined;
  const interviewerEmail = asRecord.interviewer_email_config as
    | BackendEmailCommConfig
    | undefined;
  const selfScheduleEmail = asRecord.candidate_self_schedule_email_config as
    | BackendEmailCommConfig
    | undefined;

  const toCommConfig = (t: BackendCommTemplate | undefined) =>
    t
      ? {
          subject: t.subject ?? '',
          body: t.body ?? '',
          attachments: Array.isArray(t.attachments) ? t.attachments : [],
          comm_template_id: t.id,
        }
      : undefined;

  const toEmailCommConfig = (e: BackendEmailCommConfig | undefined) =>
    e && Object.keys(e).length > 0
      ? {
          subject: e.subject ?? '',
          body: e.body ?? '',
          attachments: Array.isArray(e.attachments) ? e.attachments : [],
          comm_template_id: e.comm_template_id,
          from_user_id: e.from_user_id,
          cc_user_ids: e.cc_user_ids ?? [],
          bcc_user_ids: e.bcc_user_ids ?? [],
        }
      : undefined;

  return {
    send_calendar_invite_to_candidate: Boolean(
      asRecord.send_calendar_invite_to_candidate ?? true
    ),
    send_calendar_invite_to_interviewer: Boolean(
      asRecord.send_calendar_invite_to_interviewer ?? true
    ),
    send_email_to_candidate: Boolean(asRecord.send_email_to_candidate),
    send_email_to_interviewer: Boolean(asRecord.send_email_to_interviewer),
    send_ics_to_candidate: Boolean(asRecord.send_ics_to_candidate),
    send_ics_to_interviewer: Boolean(asRecord.send_ics_to_interviewer),
    calendar_invite_candidate: toCommConfig(candidateCal),
    calendar_invite_interviewer: toCommConfig(reviewerCal),
    candidate_email_comm_config: toEmailCommConfig(candidateEmail),
    interviewer_email_comm_config: toEmailCommConfig(interviewerEmail),
    candidate_self_schedule_email_comm_config:
      toEmailCommConfig(selfScheduleEmail),
    self_schedule_section_enabled: Boolean(
      asRecord.self_schedule_section_enabled
    ),
  };
}

/**
 * Map the scorecard shape returned by GET /ats_interviews/:id into
 * the wizard's internal ScorecardStep shape.
 *   backend:  { scorecard_required, scorecard_config: { is_feedback_mandatory, questions }, scorecard_feedback_description, scorecard_rating_description, ats_scorecard_ids }
 *   wizard:   { enabled, ai_enabled, template_id, is_feedback_mandatory, questions, feedbackDescription, ratingDescription }
 */
function hydrateScorecardFromApi(
  interview: InterviewDetails
): Record<string, unknown> {
  const cfg = (interview.scorecard_config as
    | { questions?: unknown[]; is_feedback_mandatory?: boolean }
    | undefined) ?? { questions: [], is_feedback_mandatory: false };
  return {
    enabled: Boolean(interview.scorecard_required),
    ai_enabled: Boolean(interview.fill_with_ai_enabled),
    template_id: '',
    is_feedback_mandatory: Boolean(cfg.is_feedback_mandatory),
    questions: Array.isArray(cfg.questions) ? cfg.questions : [],
    feedbackDescription:
      (interview.scorecard_feedback_description as string | undefined) ?? '',
    ratingDescription:
      (interview.scorecard_rating_description as string | undefined) ?? '',
  };
}

/**
 * Map `/ats_schedules/:id`'s candidate-slot-selection email to the
 * step's stored shape. Backend uses two distinct payload keys here:
 *
 *   - `candidate_mail_comm_template` — the slot-selection email body
 *     (category `candidate_schedule_notice`). Subject/body we render.
 *   - `candidate_mail_from` — the sender user object. Its `id` seeds
 *     the From select.
 *   - `candidate_mail_cc_participants` / `_bcc_participants` — cc/bcc.
 *
 * Confusingly, `candidate_email_config` ALSO exists on the same
 * response but is the post-booking *interview* notice (category
 * `candidate_interview_notice`) — that belongs to the Communication
 * step, not here. Read from `candidate_mail_comm_template` only.
 *
 * We pre-convert to `EmailCompositionValue` keys so the step's lazy
 * init (`'content' in existing`) consumes this directly.
 */
function hydrateSlotSelectionEmailFromApi(
  interview: InterviewDetails | Record<string, unknown>
): Record<string, unknown> | undefined {
  const asRecord = interview as Record<string, unknown>;
  const template = asRecord.candidate_mail_comm_template as
    | {
        id?: number;
        subject?: string | null;
        body?: string | null;
        attachments?: unknown[];
      }
    | undefined;
  const from = asRecord.candidate_mail_from as
    | { id?: number | string }
    | undefined;
  const cc = asRecord.candidate_mail_cc_participants as
    | Array<{ id?: number }>
    | undefined;
  const bcc = asRecord.candidate_mail_bcc_participants as
    | Array<{ id?: number }>
    | undefined;
  if (!template || typeof template !== 'object') return undefined;
  const fromValue =
    from &&
    typeof from === 'object' &&
    from.id !== undefined &&
    from.id !== null
      ? String(from.id)
      : '';
  return {
    subject: template.subject ?? '',
    content: template.body ?? '',
    from: fromValue,
    cc: Array.isArray(cc)
      ? cc.map((u) => (u.id !== undefined ? String(u.id) : '')).filter(Boolean)
      : [],
    bcc: Array.isArray(bcc)
      ? bcc.map((u) => (u.id !== undefined ? String(u.id) : '')).filter(Boolean)
      : [],
    template: null,
    // Round-trip the template id as `candidate_mail_template_id` on
    // save even when the user didn't re-pick a template.
    _hydrated_template_id: template.id ?? null,
  };
}

/**
 * Build the full wizard payload from an existing interview.
 */
export function hydratePayloadFromApi(
  interview: InterviewDetails
): ScheduleInterviewPayload {
  const details = hydrateDetailsFromApi(interview);
  const communication = hydrateCommunicationFromApi(interview);
  const scorecard = hydrateScorecardFromApi(interview);
  const slotSelectionEmail = hydrateSlotSelectionEmailFromApi(interview);
  return {
    details,
    communication,
    scorecard,
    ...(slotSelectionEmail ? { slotSelectionEmail } : {}),
  };
}
